/**
 * MusicLyricsSyncToggle - 歌詞の同期粒度（文字 / 行 / なし）を切り替えるセグメント
 *
 * 選んだ粒度は端末の表示設定として保存する。歌詞データが持っていない粒度は押せない。
 */

import { cn } from '@/lib/utils'
import { type LyricsSyncMode, useUISettingsStore } from '@/store/useUISettingsStore'
import type { TranscriptLine } from '@/types/audiobook-transcript'

import { hasWordLevelTiming, isUntimedLyrics } from './lyrics-timing'

const MODE_OPTIONS: { value: LyricsSyncMode; label: string }[] = [
  { value: 'word', label: '文字' },
  { value: 'line', label: '行' },
  { value: 'none', label: 'なし' },
]

export const MusicLyricsSyncToggle = ({ lines }: { lines: TranscriptLine[] }) => {
  const syncMode = useUISettingsStore((s) => s.lyricsSyncMode)
  const setSyncMode = useUISettingsStore((s) => s.setLyricsSyncMode)

  const isUntimed = isUntimedLyrics(lines)
  const canUseWord = !isUntimed && hasWordLevelTiming(lines)

  const isDisabled = (mode: LyricsSyncMode): boolean => {
    if (mode === 'none') return false
    if (mode === 'line') return isUntimed
    return !canUseWord
  }

  return (
    <div className="flex items-center gap-2 px-2 pb-1">
      <span className="text-xs text-muted-foreground">同期</span>
      <div className="flex gap-0.5 rounded-full bg-muted p-0.5">
        {MODE_OPTIONS.map((option) => {
          const disabled = isDisabled(option.value)
          const isSelected = syncMode === option.value

          return (
            <button
              key={option.value}
              type="button"
              disabled={disabled}
              onClick={() => setSyncMode(option.value)}
              className={cn(
                'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                isSelected ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground',
                disabled && 'opacity-40',
              )}
            >
              {option.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
